import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaPhone, FaIdCard, FaHome, FaBuilding, FaFileInvoiceDollar} from 'react-icons/fa';
import axios from '../api/axios';

export default function TerceroDetail() {
  const { documento } = useParams();
  const [tercero, setTercero] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTerceroData = async () => {
      setIsLoading(true);
      try {
        const [terceroResponse, bancosResponse, facturasResponse] = await Promise.all([
          axios.get(`/terceros/${documento}`),
          axios.get(`/bancos?documentoTercero=${documento}`),
          axios.get(`/facturas?terceroId=${documento}`)
        ]);

        setTercero(terceroResponse.data);
        setBancos(bancosResponse.data);
        setFacturas(facturasResponse.data);
      } catch (err) {
        setError('Error al cargar los detalles del tercero. Por favor, intente de nuevo.');
        console.error('Error fetching tercero details:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTerceroData();
  }, [documento]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <Link to="/home/terceros" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800">
          <FaArrowLeft className="mr-2" /> Volver a la lista de terceros
        </Link>
      </div>
    );
  }

  if (!tercero) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded" role="alert">
          <p className="font-bold">Advertencia</p>
          <p>No se encontró el tercero</p>
        </div>
        <Link to="/home/terceros" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800">
          <FaArrowLeft className="mr-2" /> Volver a la lista de terceros
        </Link>
      </div>
    );
  }

  const formatCurrency = (value) => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    } else if (typeof value === 'string') {
      return parseFloat(value).toFixed(2);
    }
    return '0.00';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Link to="/home/terceros" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors duration-200">
        <FaArrowLeft className="mr-2" /> Volver a la lista de terceros
      </Link>
      <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-3xl font-bold flex items-center">
              <FaUser className="mr-4 text-4xl" />
              {tercero.nombre}
            </h2>
          </div>
          <p className="mt-2 text-blue-100 flex items-center">
            <FaIdCard className="mr-2" /> {tercero.tipoDocumento}: {tercero.documento}
          </p>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <FaIdCard className="mr-2 text-blue-600" /> Información de Identificación
              </h3>
              <p className="text-gray-700"><strong className="text-gray-900">Tipo de Documento:</strong> {tercero.tipoDocumento}</p>
              <p className="text-gray-700"><strong className="text-gray-900">Documento:</strong> {tercero.documento}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <FaPhone className="mr-2 text-green-600" /> Contacto
              </h3>
              <p className="text-gray-700"><strong className="text-gray-900">Teléfono:</strong> {tercero.telefono}</p>
              <p className="text-gray-700"><strong className="text-gray-900">Email:</strong> {tercero.email || 'No disponible'}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaHome className="mr-2 text-orange-600" /> Dirección
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p className="text-gray-700"><strong className="text-gray-900">Domicilio:</strong> {tercero.domicilio}</p>
              <p className="text-gray-700"><strong className="text-gray-900">Código Postal:</strong> {tercero.codigoPostal}</p>
              <p className="text-gray-700"><strong className="text-gray-900">Provincia:</strong> {tercero.provincia}</p>
              <p className="text-gray-700"><strong className="text-gray-900">País:</strong> {tercero.pais}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaBuilding className="mr-2 text-purple-600" /> Bancos Asociados
            </h3>
            <p className="text-gray-700 mb-2">{bancos.length} banco(s) asociado(s)</p>
            {bancos.length > 0 && (
              <ul className="space-y-2">
                {bancos.map((banco, index) => (
                  <li key={index} className="bg-white p-3 rounded shadow">
                    <strong className="text-gray-900">{banco.nombreBanco}</strong> - IBAN: {banco.iban}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaFileInvoiceDollar className="mr-2 text-yellow-600" /> Facturas Asociadas
            </h3>
            <p className="text-gray-700 mb-2">{facturas.length} factura(s) asociada(s)</p>
            {facturas.length > 0 && (
              <ul className="space-y-2">
                {facturas.map((factura, index) => (
                  <li key={index} className="bg-white p-3 rounded shadow flex justify-between items-center">
                    <span><strong className="text-gray-900">Factura #{factura.numero}</strong></span>
                    <span className="text-green-600 font-semibold">{formatCurrency(factura.totalConIva)} €</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}